import isEqual from 'lodash.isequal';
import { createStore } from 'zustand';

import type { AgentChat } from '@cadstrom/cadstrom-shared';

/** Lifecycle of the active workbench run, mirrored from the AI SDK workbench helper. */
export type WorkbenchRunStatus = 'submitted' | 'streaming' | 'ready' | 'error';

/** Statuses meaning a run is in flight. */
const ACTIVE_STATUSES: readonly WorkbenchRunStatus[] = ['submitted', 'streaming'];

/** State and actions for one workbench pane's run-status store. */
export interface WorkbenchRunStatusStoreState {
	/** Status of the most recent run. */
	status: WorkbenchRunStatus;
	/** What went wrong, when `status` is `'error'`; undefined otherwise. */
	errorMessage: string | undefined;
	/**
	 * When the runtime first saw the current run as active, or null while idle.
	 * Recorded here rather than where it is displayed, so the reading does not
	 * restart every time a piece of chrome mounts.
	 *
	 * This is when *this client* saw the run, not when the server started it: a
	 * run already in flight before the page attached is measured from the attach.
	 * Closing that gap needs a start time on the run-state response.
	 */
	runStartedAt: number | null;
	/**
	 * Annotations the active thread's tools attached to their calls, keyed by
	 * tool call id. Carried here because assistant-ui's converted parts drop
	 * `toolMetadata`, and the original AI SDK messages live with the thread
	 * runtime — a sibling of the panel.
	 */
	toolCallAnnotations: ReadonlyMap<string, AgentChat.ToolCallAnnotations>;
	/** Records the run's status. Ignores reports that change nothing. */
	report: (update: { status: WorkbenchRunStatus; errorMessage: string | undefined }) => void;
	/** Records the active thread's tool call annotations. Ignores reports that change nothing. */
	reportToolCallAnnotations: (annotations: ReadonlyMap<string, AgentChat.ToolCallAnnotations>) => void;
}

/**
 * Creates a store scoped to a single workbench pane, carrying state the thread
 * runtime owns — which assistant-ui hosts as a sibling of the panel — across
 * to the panel's own components: the run lifecycle, and the per-call tool
 * annotations that assistant-ui's message conversion drops.
 *
 * @returns A vanilla Zustand store holding {@link WorkbenchRunStatusStoreState}.
 */
export function createWorkbenchRunStatusStore() {
	return createStore<WorkbenchRunStatusStoreState>()((set, get) => ({
		status: 'ready',
		errorMessage: undefined,
		runStartedAt: null,
		toolCallAnnotations: new Map(),
		reportToolCallAnnotations: (annotations) => {
			if (isEqual(get().toolCallAnnotations, annotations)) {
				return;
			}
			set({ toolCallAnnotations: annotations });
		},
		report: ({ status, errorMessage }) => {
			const current = get();
			if (current.status === status && current.errorMessage === errorMessage) {
				return;
			}
			const isActive = ACTIVE_STATUSES.includes(status);
			const wasActive = ACTIVE_STATUSES.includes(current.status);
			set({
				status,
				errorMessage,
				// Held across submitted → streaming so the reading is continuous for one run.
				runStartedAt:
					!isActive ? null
					: wasActive ? current.runStartedAt
					: Date.now(),
			});
		},
	}));
}

export type WorkbenchRunStatusStoreApi = ReturnType<typeof createWorkbenchRunStatusStore>;
